import * as React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { KeyRound, UserCheck, GitPullRequest, CheckCircle2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  useDocumentMutations,
  useChangeRequestMutations,
} from '@/features/matters/hooks/useMatters';
import { useCase360Governance } from '../hooks/useCase360Governance';
import { dashboardQueryKeys } from '../api/dashboard.api';

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Refresh the dashboard tiles + governance lists after an approval action. */
function useDashboardInvalidate() {
  const queryClient = useQueryClient();
  return React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: dashboardQueryKeys.case360Governance() });
    void queryClient.invalidateQueries({
      queryKey: dashboardQueryKeys.case360PendingApprovals(),
    });
  }, [queryClient]);
}

function EmptyState() {
  const { t } = useTranslation('common');
  return (
    <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-400">
      <CheckCircle2 size={16} className="text-emerald-400" />
      {t('dashboard.case360.noPendingItems')}
    </div>
  );
}

const ROW = 'rounded-lg border border-slate-200 p-3';

// ── Break-glass requests (Accept / Deny) ─────────────────────────────────────
export function BreakGlassRequestsDialog({ open, onOpenChange }: DialogProps) {
  const { t } = useTranslation('common');
  const { data } = useCase360Governance(open);
  const items = data?.approvals?.breakGlass ?? [];
  const { grantBreakGlassMutation, denyBreakGlassMutation } = useDocumentMutations();
  const invalidate = useDashboardInvalidate();
  const busy = grantBreakGlassMutation.isPending || denyBreakGlassMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-rose-500" />
            {t('dashboard.case360.breakGlassDialogTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('dashboard.case360.breakGlassDialogDesc')}
          </DialogDescription>
        </DialogHeader>
        {items.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="max-h-[60vh] space-y-2 overflow-y-auto">
            {items.map((g) => (
              <li key={g.id} className={ROW}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Link
                      to={`/app/matters/${g.document?.matterId}?tab=documents`}
                      onClick={() => onOpenChange(false)}
                      className="font-medium text-[#0069D1] hover:underline"
                    >
                      {g.document?.name ?? `#${g.document?.id}`}
                    </Link>
                    <p className="text-xs text-slate-500">
                      {t('dashboard.case360.requestedBy', { name: g.auditor?.name ?? '—' })}
                    </p>
                    {g.reason && (
                      <p className="mt-0.5 text-xs text-slate-600">{g.reason}</p>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy || !g.document || !g.auditor}
                      className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                      onClick={() =>
                        g.document &&
                        g.auditor &&
                        grantBreakGlassMutation.mutate(
                          {
                            documentId: g.document.id,
                            matterId: g.document.matterId,
                            data: { auditorId: g.auditor.id, reason: g.reason },
                          },
                          { onSuccess: invalidate },
                        )
                      }
                    >
                      {t('accept')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy || !g.document || !g.auditor}
                      className="text-red-700 border-red-300 hover:bg-red-50"
                      onClick={() =>
                        g.document &&
                        g.auditor &&
                        denyBreakGlassMutation.mutate(
                          {
                            documentId: g.document.id,
                            matterId: g.document.matterId,
                            data: { auditorId: g.auditor.id },
                          },
                          { onSuccess: invalidate },
                        )
                      }
                    >
                      {t('deny')}
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Lead hand-offs (info only, no actions) ───────────────────────────────────
export function DelegationsDialog({ open, onOpenChange }: DialogProps) {
  const { t } = useTranslation('common');
  const { data } = useCase360Governance(open);
  const items = data?.approvals?.delegations ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="h-4 w-4 text-amber-500" />
            {t('dashboard.case360.delegationsDialogTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('dashboard.case360.delegationsDialogDesc')}
          </DialogDescription>
        </DialogHeader>
        {items.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="max-h-[60vh] space-y-2 overflow-y-auto">
            {items.map((d) => (
              <li key={d.id} className={ROW}>
                <Link
                  to={`/app/matters/${d.matterId}`}
                  onClick={() => onOpenChange(false)}
                  className="font-medium text-[#0069D1] hover:underline"
                >
                  {d.matterName ?? `#${d.matterId}`}
                </Link>
                <p className="text-xs text-slate-600">
                  {(d.fromUserName ?? '—') + ' → ' + (d.toUserName ?? '—')}
                </p>
                {d?.initiatedByName && (
                  <p className="text-xs text-slate-400">
                    {t('dashboard.case360.requestedBy', { name: d.initiatedByName })}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Change requests (Approve / Reject) ───────────────────────────────────────
export function ChangeRequestsDialog({ open, onOpenChange }: DialogProps) {
  const { t } = useTranslation('common');
  const { data } = useCase360Governance(open);
  const items = data?.approvals?.changeRequests ?? [];
  const { approveCrMutation, rejectCrMutation } = useChangeRequestMutations();
  const invalidate = useDashboardInvalidate();

  const [rejectId, setRejectId] = React.useState<number | null>(null);
  const [reason, setReason] = React.useState('');
  const busy = approveCrMutation.isPending || rejectCrMutation.isPending;

  const closeReject = () => {
    setRejectId(null);
    setReason('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitPullRequest className="h-4 w-4 text-violet-500" />
            {t('dashboard.case360.changeRequestsDialogTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('dashboard.case360.changeRequestsDialogDesc')}
          </DialogDescription>
        </DialogHeader>
        {items.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="max-h-[60vh] space-y-2 overflow-y-auto">
            {items.map((cr) => (
              <li key={cr.id} className={ROW}>
                <Link
                  to={`/app/matters/${cr.matter?.id}?tab=plan`}
                  onClick={() => onOpenChange(false)}
                  className="font-medium text-[#0069D1] hover:underline"
                >
                  {cr.matter?.name ?? `#${cr.matter?.id}`}
                </Link>
                <p className="text-xs text-slate-500">
                  {t(`cr.type.${cr.type}`)} · {cr.author?.name ?? '—'}
                </p>
                {cr.reason && (
                  <p className="mt-0.5 text-xs text-slate-600">{cr.reason}</p>
                )}

                {rejectId === cr.id ? (
                  <div className="mt-2 space-y-2">
                    <Textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder={t('dashboard.case360.rejectReasonPlaceholder')}
                      rows={2}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy || !reason.trim() || !cr.matter}
                        className="text-red-700 border-red-300 hover:bg-red-50"
                        onClick={() =>
                          cr.matter &&
                          rejectCrMutation.mutate(
                            {
                              crId: cr.id,
                              matterId: cr.matter.id,
                              rejectReason: reason.trim(),
                            },
                            {
                              onSuccess: () => {
                                invalidate();
                                closeReject();
                              },
                            },
                          )
                        }
                      >
                        {t('dashboard.case360.confirmReject')}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={closeReject}>
                        {t('cancel')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy || !cr.matter}
                      className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                      onClick={() =>
                        cr.matter &&
                        approveCrMutation.mutate(
                          { crId: cr.id, matterId: cr.matter.id },
                          { onSuccess: invalidate },
                        )
                      }
                    >
                      {t('cr.approve')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-700 border-red-300 hover:bg-red-50"
                      onClick={() => setRejectId(cr.id)}
                    >
                      {t('cr.reject')}
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
